import ArticleDetailShimmer, { ArticleCommentsShimmer } from "./ArticleDetailShimmer";
import SafeHtml from '../../components/SafeHtml';
﻿import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import DOMPurify from "dompurify";
import Swal from "sweetalert2";
import HomeDashboardLayout from "../HomeDashboardLayout";
import MyWalletSidebar from "../MyWalletSidebar";
import "../../dashboard/Dashboard.css";
import "../HomeDashboard.css";
import "../questionBank/QbankDetail.css";
import "../notes/Notesdetails.css";
import "./Articledetails.css";
import HeaderProfileSection from "../HeaderProfileSection";
import AllComment from "./AllComment";
import { useSidebarData } from "../../hooks/useSidebarData";
import { useBlogActions } from "../../hooks/useBlogActions";
import { useBlogComments, useBlogDetail } from "../../hooks/useBlogDetail";
import { useBlogList } from "../../hooks/useBlogList";
import { createBlogComment, storeBlogReadingTime, toggleBlogCommentLike } from "../../services/blogService";
import { resolveQuestionBankStateId } from "../../questionBank/stateUtils";
import { CONTENT_ROUTES } from "../contentRoutes";

import SearchDetailLayout from '../search/SearchDetailLayout';
import { SearchInlineShimmer } from '../search/SearchShimmer';

const ArticleDetails = ({ searchContext = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { data: sidebarData = null } = useSidebarData();
  const stateId = useMemo(() => searchContext?.stateId || resolveQuestionBankStateId({ sidebarData, locationState: location.state }), [searchContext?.stateId, location.state, sidebarData]);
  const searchBlogId = Number(new URLSearchParams(location.search).get("blog_id") || 0);
  const requestedBlogId = Number(searchContext?.blogId || location.state?.blogId || location.state?.blog_id || searchBlogId);
  const listContext = searchContext ? { search: searchContext.keyword } : location.state?.articleListContext || {};
  const listFilters = listContext.filters || {};
  const { data: fallbackList, isLoading: isFallbackLoading } = useBlogList({ is_mobile: 1, is_blog_list: 1, page: 1, ...(stateId ? { states_id: stateId } : {}) }, { enabled: !requestedBlogId });
  const blogId = requestedBlogId || Number(fallbackList?.blogs?.[0]?.id || 0);
  const [commentText, setCommentText] = useState("");
  const [commentsPage, setCommentsPage] = useState(1);
  const [allComments, setAllComments] = useState([]);
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [repliesPage, setRepliesPage] = useState(1);
  const [allReplies, setAllReplies] = useState([]);
  const startedAtRef = useRef(null);
  const elapsedSecondsRef = useRef(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const detailParams = useMemo(() => ({
    is_mobile: 1,
    blog_id: blogId,
    ...(stateId ? { states_id: stateId } : {}),
    ...(listFilters.read_unread_id ? { read_unread_id: listFilters.read_unread_id } : {}),
    ...(listFilters.blog_category_id ? { category_id: listFilters.blog_category_id } : {}),
    ...(listFilters.tag_id ? { tag_id: listFilters.tag_id } : {}),
    ...(listFilters.bookmark_status ? { bookmark_status: 1 } : {}),
    ...(listContext.search ? { search: listContext.search, search_term: listContext.search } : {}),
  }), [blogId, listContext.search, listFilters.blog_category_id, listFilters.bookmark_status, listFilters.read_unread_id, listFilters.tag_id, stateId]);
  const { data: article, isLoading, isError, error } = useBlogDetail(detailParams);
  const { data: commentsData, isLoading: commentsLoading, isFetching: commentsFetching } = useBlogComments({ blog_id: blogId, page: commentsPage });
  const { data: repliesData, isLoading: repliesLoading, isFetching: repliesFetching } = useBlogComments({ blog_id: blogId, comment_id: replyTo?.comment_id, page: repliesPage }, { enabled: Boolean(replyTo?.comment_id) });
  const { toggleLike, toggleBookmark, toggleReadStatus } = useBlogActions();
  const createComment = useMutation({ mutationFn: createBlogComment });
  const toggleCommentLike = useMutation({ mutationFn: toggleBlogCommentLike });

  useEffect(() => {
    const comments = commentsData?.comments || [];
    setAllComments((current) => commentsPage === 1 ? comments : [...current, ...comments.filter((comment) => !current.some((existing) => Number(existing.comment_id) === Number(comment.comment_id)))]);
  }, [commentsData?.comments, commentsPage]);

  useEffect(() => {
    if (!replyTo?.comment_id) {
      setRepliesPage(1);
      setAllReplies([]);
      return;
    }
    const replies = repliesData?.comments || [];
    setAllReplies((current) => repliesPage === 1 ? replies : [...current, ...replies.filter((reply) => !current.some((existing) => Number(existing.comment_id) === Number(reply.comment_id)))]);
  }, [repliesData?.comments, repliesPage, replyTo?.comment_id]);

  useEffect(() => {
    if (!article?.id) return undefined;
    const initialSeconds = Math.max(0, Number(article.timerSeconds) || 0);
    startedAtRef.current = Date.now();
    elapsedSecondsRef.current = initialSeconds;
    setElapsedSeconds(initialSeconds);
    const timerId = window.setInterval(() => {
      const nextSeconds = initialSeconds + Math.floor((Date.now() - startedAtRef.current) / 1000);
      elapsedSecondsRef.current = nextSeconds;
      setElapsedSeconds(nextSeconds);
    }, 1000);
    return () => {
      window.clearInterval(timerId);
      if (!stateId || !startedAtRef.current) return;
      storeBlogReadingTime({ blog_id: article.id, states_id: stateId, timer_seconds: elapsedSecondsRef.current }).catch(() => {});
    };
  }, [article?.id, article?.timerSeconds, stateId]);

  const formatDuration = (seconds) => {
    const value = Math.max(0, Number(seconds) || 0);
    return [Math.floor(value / 3600), Math.floor((value % 3600) / 60), value % 60].map((part) => String(part).padStart(2, "0")).join(":");
  };

  const showError = (requestError) => Swal.fire({ icon: "error", title: "Action unavailable", text: requestError?.response?.data?.message || requestError?.message || "Please try again." });
  const updateArticle = (update) => queryClient.setQueryData(["dashboardBlogDetail", detailParams], (current) => current ? { ...current, ...update(current) } : current);

  const handleLike = async () => {
    try {
      const result = await toggleLike.mutateAsync({ blog_id: article.id });
      updateArticle(() => ({ isLiked: Number(result.like_status) === 1, totalLikes: Number(result.total_likes || 0) }));
    } catch (requestError) { showError(requestError); }
  };
  const handleBookmark = async () => {
    try {
      const result = await toggleBookmark.mutateAsync({ blog_id: article.id });
      updateArticle(() => ({ isBookmarked: Number(result.bookmark_status) === 1 }));
    } catch (requestError) { showError(requestError); }
  };
  const handleRead = async () => {
    if (!stateId) return;
    try {
      const result = await toggleReadStatus.mutateAsync({ blog_id: article.id, states_id: stateId });
      updateArticle(() => ({ isRead: Number(result.view_status) === 1 }));
    } catch (requestError) { showError(requestError); }
  };
  const handleCommentSubmit = async () => {
    if (!commentText.trim() || !stateId) return;
    try {
      await createComment.mutateAsync({ blog_id: article.id, state_id: stateId, comment: commentText.trim() });
      setCommentText("");
      setCommentsPage(1);
      queryClient.invalidateQueries({ queryKey: ["dashboardBlogComments"] });
    } catch (requestError) { showError(requestError); }
  };
  const handleCommentLike = async (comment) => {
    try {
      const result = await toggleCommentLike.mutateAsync({ comment_id: comment.comment_id });
      const updateComment = (items) => items.map((item) => Number(item.comment_id) === Number(comment.comment_id) ? { ...item, like_status: result.like_status, total_likes: result.total_likes } : item);
      setAllComments(updateComment);
      setAllReplies(updateComment);
      if (replyTo) setReplyTo((current) => current && Number(current.comment_id) === Number(comment.comment_id) ? { ...current, like_status: result.like_status, total_likes: result.total_likes } : current);
    } catch (requestError) { showError(requestError); }
  };
  const handleReplySubmit = async () => {
    if (!replyTo?.comment_id || !replyText.trim() || !stateId) return;
    try {
      await createComment.mutateAsync({ blog_id: article.id, state_id: stateId, comment: replyText.trim(), parent_id: replyTo.comment_id });
      setReplyText("");
      setReplyTo((current) => current ? { ...current, child_count: Number(current.child_count || 0) + 1 } : current);
      setAllComments((items) => items.map((item) => Number(item.comment_id) === Number(replyTo.comment_id) ? { ...item, child_count: Number(item.child_count || 0) + 1 } : item));
      queryClient.invalidateQueries({ queryKey: ["dashboardBlogComments"] });
    } catch (requestError) { showError(requestError); }
  };
  const handleShare = async () => {
    const shareData = { title: article.title, url: window.location.href };
    if (navigator.share) await navigator.share(shareData);
    else await navigator.clipboard.writeText(shareData.url);
  };
  const returnToFilteredList = (filterUpdate) => {
    const nextContext = { filters: { ...listFilters, ...filterUpdate }, search: "" };
    navigate(CONTENT_ROUTES.articles, { replace: true, state: { ...location.state, articleListContext: nextContext } });
  };
  const handleCategorySelect = (categoryId) => {
    if (!listContext.search && categoryId) returnToFilteredList({ blog_category_id: categoryId, tag_id: "" });
  };
  const handleTagSelect = (tagId) => {
    if (!listContext.search && tagId) returnToFilteredList({ tag_id: tagId, blog_category_id: "" });
  };

  const footerStats = article ? [
    { key: "bookmark", icon: article.isBookmarked ? "bi-bookmark-fill" : "bi-bookmark", label: "Bookmark", onClick: handleBookmark, pending: toggleBookmark.isPending },
    { key: "like", icon: article.isLiked ? "bi-heart-fill" : "bi-heart", label: "Like", value: article.totalLikes, onClick: handleLike, pending: toggleLike.isPending },
    { key: "view", icon: "bi-eye", label: "View", value: article.totalViews },
    { key: "comment", icon: "bi-chat-left-text-fill", label: "Comment", value: article.totalComments },
    { key: "share", icon: "bi-share", label: "Share", onClick: handleShare },
  ] : [];

  const renderFooterNav = (variant) => <div className={`footer-nav-fixed ${variant}`}><div className="qbank-stats">{footerStats.map((stat) => <button key={stat.key} type="button" className={`qbank-stat${stat.key === "share" ? " qbank-stat--share" : ""}`} onClick={stat.onClick} disabled={stat.pending || !stat.onClick}><i className={`bi ${stat.icon}`} /><span>{stat.label}</span>{stat.value !== undefined ? <strong>{stat.value}</strong> : null}</button>)}</div></div>;

  if (!blogId && isFallbackLoading) return <ArticleDetailShimmer />;
  if (!blogId) return <HomeDashboardLayout showHeader={false}><div className="choose-state__status choose-state__status--error">No articles are available.</div></HomeDashboardLayout>;
  if (isLoading && searchContext) return <SearchDetailLayout variant="article" title="News & Articles" valid query={{ isLoading: true }} goBack={() => navigate(searchContext.backTarget.pathname, { state: searchContext.backTarget.state })} />;
  if (isLoading && !article) return <ArticleDetailShimmer />;
  if (isError || !article) return <HomeDashboardLayout showHeader={false}><div className="choose-state__status choose-state__status--error">{error?.message || "Unable to load article."}</div></HomeDashboardLayout>;

  return<>   <style>{`
 .qb-title {
    font-size: 26px;
}
.qbank-card--question h3 {
    font-size: 21px;
    font-weight: 700;
}
@media (max-width: 767px){
   .qb-title {
    font-size: 20px;
}
.qbank-card--question h3 {
    font-size: 18px;
}
   }
      `}</style> <HomeDashboardLayout showHeader={false} variant="article-detail-layout">
    <div className="categories-page qbank-detail-page detailArticle" style={{ background: "#fff" }}>
      <div className="categories-page-header"><div><button type="button" className="categories-back-button" onClick={() => searchContext?.backTarget ? navigate(searchContext.backTarget.pathname, { state: searchContext.backTarget.state }) : navigate(-1)}><i className="bi bi-chevron-left" /></button></div><div><h1 className="categories-page-title"><span className="qbank-timer" aria-label="Reading timer"><i className="bi bi-clock" aria-hidden="true" />{formatDuration(elapsedSeconds)}</span></h1></div><div><HeaderProfileSection /></div></div>
      <div className="qb-Detail-content"><div className="d-flex qbank-detail-main">
        <section className="qbank-LeftContent" aria-label="Article content"><div className="qbank-card qbank-card--question">
          <p className="descriptionMain">{article.stateName}<span className="slash">{article.categoryName}</span></p>
          <div className="rapid-fire-header d-flex align-items-center justify-space-between"><h1 className="qb-title">{searchContext ? <SafeHtml as="span" inline value={article.title} /> : article.title}</h1><label className="qbank-guess article-mark"><span className="qbank-guess__check"><input type="checkbox" checked={article.isRead} onChange={handleRead} disabled={toggleReadStatus.isPending} /><span className="qbank-guess__box" /></span><span className="qbank-guess__label"> Mark as Read </span></label></div>
          <div className="article-categories"><span className="category-label">Categories:</span>{article.categories.map((category) => <button type="button" className="category-item" key={category.category_id} onClick={() => handleCategorySelect(category.category_id)} disabled={Boolean(listContext.search)}>{searchContext ? <SafeHtml as="span" inline value={category.name} /> : category.name}</button>)}</div>
          <div className="data-question-scroll mt-20" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.description) }} />
          {article.tags.length ? <div className="article-tags"><strong className="tags-label">Tags:</strong><div className="tags-list">{article.tags.map((tag) => <button type="button" className="tag-item" key={tag.tag_id} onClick={() => handleTagSelect(tag.tag_id)} disabled={Boolean(listContext.search)}>#{searchContext ? <SafeHtml as="span" inline value={tag.name} /> : tag.name}</button>)}</div></div> : null}
          {renderFooterNav("footer-nav-fixed--desktop")}
        </div></section>
        <aside className="qbank-rightContent" aria-label="Comments"><div className="coments-section"><div className="coments-wrapper"><label htmlFor="comments" className="coments-label">Add Comment</label><textarea id="comments" className="coments-input" placeholder="Add Comments..." value={commentText} onChange={(event) => setCommentText(event.target.value)} /><button type="button" className="qbank-stat qbank-stat--share mt-2" onClick={handleCommentSubmit} disabled={!commentText.trim() || createComment.isPending}>{createComment.isPending ? "Posting..." : "Post Comment"}</button></div></div><div><label className="coments-label">All Comment</label>{commentsLoading && !allComments.length ? searchContext ? <SearchInlineShimmer label="Loading comments" /> : <ArticleCommentsShimmer /> : <AllComment comments={allComments} onLike={handleCommentLike} onReply={(comment) => { setRepliesPage(1); setAllReplies([]); setReplyTo(comment); }} isLiking={toggleCommentLike.isPending} />}{commentsPage < Number(commentsData?.pagination?.totalPages || 1) ? <button type="button" className="article-comments__more" onClick={() => setCommentsPage((page) => page + 1)} disabled={commentsFetching}>Load more comments</button> : null}</div></aside>
      </div>{renderFooterNav("footer-nav-fixed--mobile")}</div>
      {replyTo ? <div className="article-comments__modalBackdrop" role="presentation" onMouseDown={() => { setReplyTo(null); setReplyText(""); }}><section className="article-comments__modal" role="dialog" aria-modal="true" aria-label="Comment replies" onMouseDown={(event) => event.stopPropagation()}><div className="article-comments__threadHead"><div><strong>{replyTo.username || "User"}</strong><p>{replyTo.text}</p></div><button type="button" onClick={() => { setReplyTo(null); setReplyText(""); }} aria-label="Close replies"><i className="bi bi-x-lg" /></button></div><div className="article-comments__modalBody">{repliesLoading && !allReplies.length ? searchContext ? <SearchInlineShimmer label="Loading replies" /> : <ArticleCommentsShimmer label="Loading replies" /> : <AllComment comments={allReplies} onLike={handleCommentLike} onReply={(comment) => { setRepliesPage(1); setAllReplies([]); setReplyTo(comment); }} isLiking={toggleCommentLike.isPending} />}{repliesPage < Number(repliesData?.pagination?.totalPages || 1) ? <button type="button" className="article-comments__more" onClick={() => setRepliesPage((page) => page + 1)} disabled={repliesFetching}>Load more replies</button> : null}</div><textarea className="coments-input" placeholder="Write a reply..." value={replyText} onChange={(event) => setReplyText(event.target.value)} /><button type="button" className="qbank-stat qbank-stat--share mt-2" onClick={handleReplySubmit} disabled={!replyText.trim() || createComment.isPending}>{createComment.isPending ? "Posting..." : "Post Reply"}</button></section></div> : null}
    </div>
    <MyWalletSidebar withTopOffset={false} />
  </HomeDashboardLayout>;
  </>
};

export default ArticleDetails;
